/**
 * @tanstack/react-query hooks for the Attendance module + a tiny offline
 * punch queue (PWA). Thin wrappers over lib/api/attendance.ts so pages stay
 * declarative and caching/invalidation lives in one place.
 *
 * Query-key convention:
 *   ["attendance", <area>, ...params]  — flush whole module with ["attendance"]
 *
 * OFFLINE QUEUE (plan §9.3): when a punch POST fails because the browser is
 * offline, it is queued in IndexedDB (keyed by client_punch_uuid) and flushed
 * via POST /attendance/punch/batch on the next `online` event. The server
 * dedups on client_punch_uuid so a double-flush is a no-op. Background Sync is
 * a progressive enhancement layered on top by the service worker; the
 * foreground `online` listener here is the always-available fallback.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { attendanceApi } from "@/lib/api";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import { formatDateTime } from "@/lib/format";
import type {
  BatchPunchResultItem,
  CreateLocationRequest,
  CreateRegularizationRequest,
  DecisionRequest,
  PunchRequest,
  UpdateAttendanceSettingsRequest,
  UpdateLocationRequest,
} from "@/types/attendance";
import {
  dequeueAll,
  enqueuePunch,
  removeFromQueue,
  type QueuedPunch,
} from "@/lib/attendance-offline-queue";
import { toast } from "sonner";
import { FUTURE_SKEW_RETRY_MS, planFlush } from "@/lib/attendance-flush";

/** "9 Jun 2026, 9:00 am IST (in)" — a queued punch in India time, like
 *  every other punch time (lib/format). */
function fmtQueuedPunch(q?: QueuedPunch): string {
  if (!q) return "a queued punch";
  const d = new Date(q.body.punched_at);
  if (Number.isNaN(d.getTime())) return `a queued ${q.body.punch_type} punch`;
  return `${formatDateTime(d)} (${q.body.punch_type})`;
}

/** User-facing summary of queued punches the server refused for good. */
export function describeRefusedPunches(
  refused: BatchPunchResultItem[],
  byUUID: Map<string, QueuedPunch>,
): string {
  const lines = refused.map(
    (r) =>
      `${fmtQueuedPunch(byUUID.get(r.client_punch_uuid))} — ${
        r.message ?? "its time is outside the allowed window"
      }`,
  );
  return (
    `${refused.length} queued punch${refused.length === 1 ? " was" : "es were"} ` +
    `refused and removed: ${lines.join("; ")}. ` +
    "File a regularization for the affected day to correct it."
  );
}

export const attendanceKeys = {
  all: ["attendance"] as const,
  today: () => [...attendanceKeys.all, "today"] as const,
  myDays: (from?: string, to?: string) =>
    [...attendanceKeys.all, "my-days", from ?? "", to ?? ""] as const,
  team: (params?: Record<string, unknown>) =>
    [...attendanceKeys.all, "team", JSON.stringify(params ?? {})] as const,
  days: (params?: Record<string, unknown>) =>
    [...attendanceKeys.all, "days", JSON.stringify(params ?? {})] as const,
  regularizations: (status?: string, scope?: string, excludeSelf?: boolean) =>
    [
      ...attendanceKeys.all,
      "regs",
      status ?? "all",
      excludeSelf ? "excl-self" : (scope ?? "role"),
    ] as const,
  locations: () => [...attendanceKeys.all, "locations"] as const,
  settings: () => [...attendanceKeys.all, "settings"] as const,
  punchExempt: () => [...attendanceKeys.all, "punch-exempt"] as const,
};

// ---------------------------------------------------------------------------
// Punch + today
// ---------------------------------------------------------------------------

export function useToday() {
  return useQuery({
    queryKey: attendanceKeys.today(),
    queryFn: ({ signal }) => attendanceApi.getToday(signal),
    staleTime: 15_000,
  });
}

/**
 * Punch in/out. On a network failure (offline) the punch is enqueued in
 * IndexedDB and the mutation resolves "queued" so the UI can show an
 * optimistic state; the queue flushes on reconnect.
 */
export function usePunch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PunchRequest) => {
      try {
        return { online: true as const, punch: await attendanceApi.punch(body) };
      } catch (err) {
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          await enqueuePunch(body);
          return { online: false as const, punch: null };
        }
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.today() });
      void qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

/** Result of one offline-queue flush. */
export interface FlushResult {
  /** punches the server accepted (removed from the queue). */
  flushed: number;
  /** punches kept because the device clock is ahead — retry later. */
  retryLater: number;
}

/**
 * Flushes the offline queue to POST /attendance/punch/batch. Wire this once
 * (e.g. in the punch page) so a reconnect drains queued punches. What
 * happens to each item is lib/attendance-flush.planFlush: accepted ones
 * leave the queue, too-old ones are dropped with a toast, ones refused for
 * a FAST device clock stay queued for a later retry (F113).
 */
export function useFlushOfflineQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<FlushResult> => {
      const queued = await dequeueAll();
      if (queued.length === 0) return { flushed: 0, retryLater: 0 };
      const res = await attendanceApi.punchBatch(queued.map((q) => q.body));
      const plan = planFlush(res.results);
      // Drop every item the server accepted (ok=true, dedup-safe).
      for (const uuid of plan.accepted) await removeFromQueue(uuid);
      // A punch refused as too old can never succeed (its timestamp only
      // ages further): retrying forever would wedge the queue. Drop it and
      // tell the user which punches were refused and why.
      if (plan.dropped.length > 0) {
        const byUUID = new Map(queued.map((q) => [q.client_punch_uuid, q]));
        for (const item of plan.dropped) {
          await removeFromQueue(item.client_punch_uuid);
        }
        toast.error(describeRefusedPunches(plan.dropped, byUUID), {
          duration: 15000,
        });
      }
      // Ahead of the server clock: kept, retried shortly (the drain hook
      // schedules it). Say so once, so the employee checks the clock.
      if (plan.retryLater.length > 0) {
        toast.message(
          `${plan.retryLater.length} queued punch${plan.retryLater.length === 1 ? " is" : "es are"} ahead of the server clock — ` +
            "we'll send it again in a few minutes. Check that this device's clock is right.",
          { duration: 10000 },
        );
      }
      return { flushed: plan.accepted.length, retryLater: plan.retryLater.length };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

/**
 * useOfflineQueueDrain registers an `online` listener that flushes the queue
 * automatically on reconnect. Mount once on the punch surface.
 */
export function useOfflineQueueDrain() {
  const flush = useFlushOfflineQueue();
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const onOnline = () => {
      void flush
        .mutateAsync()
        .then((res) => {
          // Future-skewed punches stayed queued: try again shortly rather
          // than waiting for the next reconnect (F113).
          if (res.retryLater > 0 && retryTimer.current === null) {
            retryTimer.current = setTimeout(() => {
              retryTimer.current = null;
              if (typeof navigator === "undefined" || navigator.onLine) onOnline();
            }, FUTURE_SKEW_RETRY_MS);
          }
        })
        .catch(() => {
          /* best-effort; the next reconnect retries */
        });
    };
    window.addEventListener("online", onOnline);
    // Drain anything left from a previous session on mount, if online.
    if (typeof navigator !== "undefined" && navigator.onLine) onOnline();
    return () => {
      window.removeEventListener("online", onOnline);
      if (retryTimer.current !== null) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ---------------------------------------------------------------------------
// Day lists
// ---------------------------------------------------------------------------

export function useMyDays(from?: string, to?: string) {
  return useQuery({
    queryKey: attendanceKeys.myDays(from, to),
    queryFn: ({ signal }) =>
      attendanceApi.listMyDays(
        { ...(from ? { from } : {}), ...(to ? { to } : {}) },
        signal,
      ),
  });
}

export function useTeamDays(params?: {
  date?: string;
  from?: string;
  to?: string;
  employee_id?: number;
  /** 1 = the review queue only (flagged days not yet reviewed). */
  needs_review?: 1;
  /** 1 = everyone in scope, including people who did not punch. */
  roster?: 1;
}) {
  return useQuery({
    queryKey: attendanceKeys.team(params),
    queryFn: ({ signal }) => attendanceApi.listTeamDays(params, signal),
  });
}

export function useDays(params?: {
  date?: string;
  from?: string;
  to?: string;
  employee_id?: number;
}) {
  return useQuery({
    queryKey: attendanceKeys.days(params),
    queryFn: ({ signal }) => attendanceApi.listDays(params, signal),
  });
}

// ---------------------------------------------------------------------------
// Regularizations
// ---------------------------------------------------------------------------

export function useRegularizations(
  status?: string,
  scope?: "self",
  opts?: { excludeSelf?: boolean },
) {
  return useQuery({
    queryKey: attendanceKeys.regularizations(status, scope, opts?.excludeSelf),
    queryFn: ({ signal }) =>
      attendanceApi.listRegularizations(
        {
          ...(status ? { status } : {}),
          ...(scope ? { scope } : {}),
          ...(opts?.excludeSelf ? { exclude_self: true } : {}),
        },
        signal,
      ),
  });
}

function invalidateAttendance(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: attendanceKeys.all });
  void qc.invalidateQueries({ queryKey: ["notifications"] });
  // The Inbox / tab badges count pending regularizations.
  invalidateApprovalsSummary(qc);
}

export function useCreateRegularization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRegularizationRequest) =>
      attendanceApi.createRegularization(body),
    onSuccess: () => invalidateAttendance(qc),
  });
}

export function useApproveRegularization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: DecisionRequest }) =>
      attendanceApi.approveRegularization(vars.id, vars.body),
    onSuccess: () => invalidateAttendance(qc),
  });
}

export function useRejectRegularization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: DecisionRequest }) =>
      attendanceApi.rejectRegularization(vars.id, vars.body),
    onSuccess: () => invalidateAttendance(qc),
  });
}

export function useCancelRegularization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => attendanceApi.cancelRegularization(id),
    onSuccess: () => invalidateAttendance(qc),
  });
}

// ---------------------------------------------------------------------------
// Office locations
// ---------------------------------------------------------------------------

export function useLocations() {
  return useQuery({
    queryKey: attendanceKeys.locations(),
    queryFn: ({ signal }) => attendanceApi.listLocations(signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLocationRequest) =>
      attendanceApi.createLocation(body),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: attendanceKeys.locations() }),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateLocationRequest }) =>
      attendanceApi.updateLocation(vars.id, vars.body),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: attendanceKeys.locations() }),
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => attendanceApi.deleteLocation(id),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: attendanceKeys.locations() }),
  });
}

// ---------------------------------------------------------------------------
// Settings + recompute
// ---------------------------------------------------------------------------

export function useAttendanceSettings() {
  return useQuery({
    queryKey: attendanceKeys.settings(),
    queryFn: ({ signal }) => attendanceApi.getSettings(signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateAttendanceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAttendanceSettingsRequest) =>
      attendanceApi.updateSettings(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.settings() });
      // The weekly off is ONE tenant setting shared with Settings → Leave
      // (GET /leave/settings weekly_off_mask): refresh that copy too, so
      // the leave-policy card never shows — or saves — a stale mask.
      void qc.invalidateQueries({ queryKey: ["leave", "settings"] });
      // Punching / Work from home (migration 167) change Today's card (the
      // menu's copy of wfh_mode is on /me — the settings page refreshes it).
      void qc.invalidateQueries({ queryKey: attendanceKeys.today() });
    },
  });
}

/** "Doesn't need to punch" list (Settings › Punching; HR admins). */
export function usePunchExempt(enabled = true) {
  return useQuery({
    queryKey: attendanceKeys.punchExempt(),
    queryFn: ({ signal }) => attendanceApi.listPunchExempt(signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Set / clear "Doesn't need to punch" for one person (HR admins; never
 *  oneself — the server answers 403 own_record). */
export function useSetPunchExempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, exempt }: { userId: number; exempt: boolean }) =>
      attendanceApi.setPunchExempt(userId, exempt),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.punchExempt() });
    },
  });
}

export function useMarkDayReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; note?: string }) =>
      attendanceApi.markDayReviewed(v.id, v.note),
    onSuccess: () => invalidateAttendance(qc),
  });
}

export function useUnmarkDayReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => attendanceApi.unmarkDayReviewed(id),
    onSuccess: () => invalidateAttendance(qc),
  });
}

export function useMarkRangeReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      from: string;
      to: string;
      employee_id?: number;
      note?: string;
    }) => attendanceApi.markRangeReviewed(body),
    onSuccess: () => invalidateAttendance(qc),
  });
}

export function useRecompute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from: string; to: string; employee_id?: number }) =>
      attendanceApi.recompute(body),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: attendanceKeys.all }),
  });
}

/**
 * Own day rows for [from, to] with the calendar filled in: a holiday,
 * weekly off, approved-leave or WFH day without an attendance record comes
 * back as a synthetic row (`synthetic: true`) instead of a gap. My Time's
 * day strip; the regularize page keeps the plain rows (useMyDays).
 */
export function useMyCalendarDays(from: string, to: string) {
  return useQuery({
    queryKey: [...attendanceKeys.myDays(from, to), "calendar"] as const,
    queryFn: ({ signal }) =>
      attendanceApi.listMyCalendarDays({ from, to }, signal),
  });
}
